import { CampaignInfluencerStage, STAGE_META, STAGE_ORDER } from "@/marketing/models/campaignInfluencer";

export const STANDARD_PIPELINE_TYPE = "standard";

export enum CampaignPipelineType {
  Standard = "standard",
  Affiliate = "affiliate",
  PerformanceSeeding = "performance_seeding",
}

export const CAMPAIGN_PIPELINE_TYPE_LABEL: Record<
  CampaignPipelineType,
  string
> = {
  [CampaignPipelineType.Standard]: "Standard",
  [CampaignPipelineType.Affiliate]: "Affiliate",
  [CampaignPipelineType.PerformanceSeeding]: "Performance Seeding",
};

export type PipelineTone =
  "neutral" | "info" | "progress" | "warning" | "success";

export type PipelineMilestone =
  | "seat_filled"
  | "content_posted"
  | "content_locked"
  | "welcome_reward_eligible"
  | "earning";

export type PipelineWaitingOn = "admin" | "creator" | "external" | "system";

export interface PipelineCreatorTask {
  id: string;
  type: string;
  label: string;
  priority: number | string;
}

export interface PipelineAdminTask {
  kind: string;
  overdueAfterDays?: number;
  pendingWhen?: string;
}

export interface PipelineStageDefinition {
  stage: string;
  labels: { admin: string; creator: string };
  tone: PipelineTone;
  milestones: PipelineMilestone[];
  // Optional so the bundled Standard fallback (which has no ownership data) stays honest.
  waitingOn?: PipelineWaitingOn;
  creatorTask?: PipelineCreatorTask;
  urgentAfterDays?: number;
  adminTask?: PipelineAdminTask;
  notification?: string;
  beforeEnter?: string[];
}

export interface PipelineTransitionPolicy {
  allowBackward: boolean;
  allowSkipWithFastTrack: boolean;
}

export interface CampaignPipelineDefinition {
  type: string;
  initialStage: string;
  transitionPolicy: PipelineTransitionPolicy;
  supportsContentSubmission: boolean;
  stages: PipelineStageDefinition[];
  eventTransitions?: unknown[];
}

// Bundled rollout fallback, derived from STAGE_ORDER/STAGE_META so there is no second copy of the Standard stages.
export const STANDARD_PIPELINE_DEFINITION: CampaignPipelineDefinition = {
  type: STANDARD_PIPELINE_TYPE,
  initialStage: STAGE_ORDER[0] ?? CampaignInfluencerStage.FormSent,
  transitionPolicy: { allowBackward: true, allowSkipWithFastTrack: true },
  supportsContentSubmission: true,
  stages: STAGE_ORDER.map((stage) => ({
    stage,
    // The Standard fallback has no creator-facing copy, so both labels share the admin label.
    labels: {
      admin: STAGE_META[stage].label,
      creator: STAGE_META[stage].label,
    },
    tone: STAGE_META[stage].tone,
    milestones: STAGE_META[stage].milestones,
  })),
  eventTransitions: [],
};
